<script lang="ts" setup>
import { computed, nextTick, ref } from 'vue';
import { AppTypography } from '~/components/base';
import type { EntityTag } from '~/types/Candidates';

// `readonly` so callers can pass tags straight off a `readonly()`-wrapped store
// or composable state without copying the array to shed DeepReadonly.
const {
  tags,
  limit = 5,
  showTypeIcon = true,
} = defineProps<{
  tags: readonly EntityTag[];
  limit?: number;
  /** Set to `false` to render text-only chips (e.g. the sources page). */
  showTypeIcon?: boolean;
}>();

const expanded = ref(false);

const visibleTags = computed(() =>
  expanded.value ? tags : tags.slice(0, limit),
);
const hiddenCount = computed(() => Math.max(0, tags.length - limit));
const hasHidden = computed(() => !expanded.value && hiddenCount.value > 0);

const list = ref<HTMLElement | null>(null);

// Expanding unmounts the button the user just activated, which would drop focus
// to <body> (WCAG 2.4.3). Move it onto the first newly revealed chip instead.
async function expand() {
  expanded.value = true;
  await nextTick();
  list.value
    ?.querySelectorAll<HTMLElement>('a.entity-tag-list__tag')
    ?.item(limit)
    ?.focus();
}
</script>

<template>
  <div
    v-if="tags.length"
    ref="list"
    role="group"
    aria-label="Tags"
    data-testid="entity-tag-list"
    class="entity-tag-list flex flex-wrap items-center gap-2"
  >
    <AppTypography
      type="footnote-bold"
      class="text-color-tag uppercase"
      aria-hidden="true"
    >
      Tags
    </AppTypography>

    <NuxtLink
      v-for="tag in visibleTags"
      :key="tag.id"
      :to="tag.to"
      data-testid="entity-tag"
      class="entity-tag-list__tag inline-flex items-center gap-1 no-underline"
    >
      <Icon
        v-if="showTypeIcon && tag.type === 'candidate'"
        name="solar:user-circle-linear"
        size="14"
        class="shrink-0"
        aria-hidden="true"
      />
      {{ tag.label }}
    </NuxtLink>

    <button
      v-if="hasHidden"
      type="button"
      data-testid="entity-tag-more"
      class="entity-tag-list__tag entity-tag-list__more"
      :aria-label="`Show ${hiddenCount} more tags`"
      @click="expand"
    >
      +{{ hiddenCount }} more
    </button>
  </div>
</template>

<style scoped lang="scss">
.entity-tag-list {
  &__tag {
    height: 24px;
    padding: 2px $spacing-sm;
    border: 1px solid $border-color-tag;
    border-radius: 9999px;
    @include font-caption;
    color: $text-color-secondary;
    background-color: $surface-color-tag;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: $surface-color-bg-2;
    }
  }

  &__more {
    cursor: pointer;
    background: none;
  }
}
</style>
